import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { ComponentProps, useCallback } from 'react';
import { useLazyGetMatterUninvoicedHoursQuery } from 'store/api/matters';
import { MatterUninvoicedHours, PricingKind } from 'types/pricelists';
import { t } from 'utils/getTranslations';

function hoursValues(hours: MatterUninvoicedHours): Record<string, string> {
    return { noInvoice: String(hours.hoursOnNoInvoice), openInvoices: String(hours.hoursOnOpenInvoices) };
}

/**
 * The confirmations of the case price changes that re-price uninvoiced work: a kind switch, following a list in
 * place of the case's own prices, a follower's currency change and a follower's price exceptions, which re-price
 * the work of their people only. Each names the hours on no invoice and on draft or reviewed invoices; a case not
 * saved yet has none, so the change goes ahead without asking.
 */
export function useCaseRepricingConfirmation(matterId: number | undefined) {
    const { openModal, closeModal } = useModal();
    const [fetchHours] = useLazyGetMatterUninvoicedHoursQuery();

    const confirm = useCallback(
        async (
            modal: (
                hours: MatterUninvoicedHours
            ) => Pick<ComponentProps<typeof ConfirmationModal>, 'titleId' | 'titleValues' | 'confirmId' | 'content'>,
            onConfirm: () => void,
            onCancel?: () => void,
            employeeIds?: number[]
        ): Promise<void> => {
            if (matterId === undefined) {
                onConfirm();
                return;
            }
            const hours = await fetchHours({ id: String(matterId), employeeIds }).unwrap();
            openModal(
                <ConfirmationModal
                    {...modal(hours)}
                    onCancel={() => {
                        closeModal();
                        onCancel?.();
                    }}
                    onConfirm={() => {
                        closeModal();
                        onConfirm();
                    }}
                />,
                () => {
                    closeModal();
                    onCancel?.();
                }
            );
        },
        [matterId, fetchHours, openModal, closeModal]
    );

    const confirmKindSwitch = useCallback(
        (pricingKind: PricingKind, onConfirm: () => void) =>
            confirm(
                (hours) => ({
                    titleId:
                        pricingKind === PricingKind.EMPLOYEE
                            ? 'pricelists.case-follow.switch-to-employee-title'
                            : 'pricelists.case-follow.switch-to-role-title',
                    confirmId: 'pricelists.case-follow.switch-action',
                    content: t('pricelists.case-follow.switch-body', hoursValues(hours)),
                }),
                onConfirm
            ),
        [confirm]
    );

    const confirmFollow = useCallback(
        (name: string, onConfirm: () => void) =>
            confirm(
                (hours) => ({
                    titleId: 'pricelists.case-follow.follow-title',
                    titleValues: { name },
                    confirmId: 'pricelists.case-follow.follow-action',
                    content: t('pricelists.case-follow.follow-body', hoursValues(hours)),
                }),
                onConfirm
            ),
        [confirm]
    );

    const confirmCurrencyChange = useCallback(
        (currency: string, onConfirm: () => void, onCancel: () => void) =>
            confirm(
                (hours) => ({
                    titleId: 'pricelists.case-follow.currency-change-title',
                    titleValues: { currency },
                    confirmId: 'pricelists.case-follow.currency-change-action',
                    content: t('pricelists.case-follow.currency-change-body', hoursValues(hours)),
                }),
                onConfirm,
                onCancel
            ),
        [confirm]
    );

    const confirmExceptionChange = useCallback(
        (employeeIds: number[], onConfirm: () => void) =>
            confirm(
                (hours) => ({
                    titleId: 'pricelists.employee.confirm-title',
                    confirmId: 'pricelists.employee.confirm-action',
                    content: t('pricelists.case-follow.exception-body', hoursValues(hours)),
                }),
                onConfirm,
                undefined,
                employeeIds
            ),
        [confirm]
    );

    return { confirmKindSwitch, confirmFollow, confirmCurrencyChange, confirmExceptionChange };
}
